<script setup>
import { definePage } from "vue-router/auto";

definePage({
  name: "apps-settings-prefijos-importacion",
});

const user = useCookie("userData");
const router = useRouter();

const cargando = ref(false);
const importando = ref(false);
const archivo = ref(null);
const items = ref([]);
const form = ref({ id: null, prefijo: "", tipo: "debe" });

const tiposOpciones = [
  { value: "debe", title: "Debe" },
  { value: "haber", title: "Haber" },
  { value: "saldo_debe", title: "Saldo debe" },
  { value: "saldo_haber", title: "Saldo haber" },
];

const headers = [
  { title: "Prefijo", key: "prefijo" },
  { title: "Tipo", key: "tipo" },
  { title: "Acciones", key: "acciones", sortable: false, width: "120px" },
];

const etiquetaTipo = v => tiposOpciones.find(t => t.value === v)?.title ?? v;

const cargar = async () => {
  cargando.value = true;
  try {
    items.value = await $api("/prefijos-cuenta-importacion-global", { method: "GET" });
  } catch {
    items.value = [];
  } finally {
    cargando.value = false;
  }
};

const importarExcel = async () => {
  const raw = archivo.value;
  const f = Array.isArray(raw) ? raw[0] : raw;
  if (!f) return;
  importando.value = true;
  try {
    const fd = new FormData();
    fd.append("file", f);
    const res = await $api("/prefijos-cuenta-importacion-global/import", {
      method: "POST",
      body: fd,
    });
    await cargar();
    console.info("Importación OK, total:", res?.total);
  } catch (e) {
    console.error(e);
  } finally {
    importando.value = false;
    archivo.value = null;
  }
};

const guardarNuevo = async () => {
  try {
    await $api("/prefijos-cuenta-importacion-global", {
      method: "POST",
      body: { prefijo: form.value.prefijo, tipo: form.value.tipo },
    });
    form.value = { id: null, prefijo: "", tipo: "debe" };
    await cargar();
  } catch (e) {
    console.error(e);
  }
};

const editar = row => {
  form.value = { id: row.id, prefijo: row.prefijo, tipo: row.tipo };
};

const cancelarEdicion = () => {
  form.value = { id: null, prefijo: "", tipo: "debe" };
};

const guardarEdicion = async () => {
  try {
    await $api(`/prefijos-cuenta-importacion-global/${form.value.id}`, {
      method: "PUT",
      body: { prefijo: form.value.prefijo, tipo: form.value.tipo },
    });
    cancelarEdicion();
    await cargar();
  } catch (e) {
    console.error(e);
  }
};

const eliminar = async row => {
  if (!confirm("¿Eliminar este prefijo?")) return;
  try {
    await $api(`/prefijos-cuenta-importacion-global/${row.id}`, { method: "DELETE" });
    await cargar();
  } catch (e) {
    console.error(e);
  }
};

onMounted(() => {
  if (user.value?.idRol !== 1) {
    router.replace("/apps/company/list");
    return;
  }
  cargar();
});
</script>

<template>
  <VRow v-if="user?.idRol === 1">
    <VCol cols="12">
      <VCard>
        <VCardTitle class="d-flex align-center gap-2">
          <VIcon icon="ri-table-line" />
          Prefijos de cuenta (importación global)
        </VCardTitle>
        <VCardText>
          <p class="text-body-2 mb-4">
            Tabla común para todas las empresas. Cada prefijo (3 o 4 dígitos) indica si el importe
            en el Excel es Debe, Haber, Saldo debe o Saldo haber. Puede importar un Excel con columnas
            <strong>CUENTA</strong> y <strong>COLUMNA DEL DATO</strong> (como «RESUMEN CUENTAS CONTABLES»).
          </p>
          <VRow class="mb-4">
            <VCol cols="12" md="6">
              <VFileInput
                v-model="archivo"
                label="Importar Excel (.xlsx)"
                accept=".xlsx,.xls"
                prepend-icon="ri-file-excel-line"
                show-size
                density="comfortable"
              />
            </VCol>
            <VCol cols="12" md="6" class="d-flex align-center">
              <VBtn color="success" :loading="importando" @click="importarExcel">
                Cargar desde Excel
              </VBtn>
            </VCol>
          </VRow>
          <VDivider class="mb-4" />
          <VRow>
            <VCol cols="12" md="3">
              <VTextField v-model="form.prefijo" label="Prefijo (3–4 dígitos)" density="comfortable" />
            </VCol>
            <VCol cols="12" md="4">
              <VSelect
                v-model="form.tipo"
                :items="tiposOpciones"
                item-title="title"
                item-value="value"
                label="Tipo de dato"
                density="comfortable"
              />
            </VCol>
            <VCol cols="12" md="5" class="d-flex align-center gap-2">
              <VBtn v-if="!form.id" color="primary" @click="guardarNuevo">Añadir</VBtn>
              <template v-else>
                <VBtn color="primary" @click="guardarEdicion">Guardar</VBtn>
                <VBtn variant="text" @click="cancelarEdicion">Cancelar</VBtn>
              </template>
            </VCol>
          </VRow>
          <VDataTable
            :headers="headers"
            :items="items"
            :loading="cargando"
            class="mt-4"
            density="compact"
          >
            <template #item.tipo="{ item }">
              <span>{{ etiquetaTipo(item.tipo) }}</span>
            </template>
            <template #item.acciones="{ item }">
              <VBtn icon size="small" variant="text" color="primary" @click="editar(item)">
                <VIcon icon="ri-pencil-line" />
              </VBtn>
              <VBtn icon size="small" variant="text" color="error" @click="eliminar(item)">
                <VIcon icon="ri-delete-bin-line" />
              </VBtn>
            </template>
          </VDataTable>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>
